import "react-image-crop/dist/ReactCrop.css";

import { CropIcon } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Subtitle, Title } from "../Text";
import { Button } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";
import { DialogHeader } from "../ui/dialog";
import { useMemo } from "react";
import { useRef, useState } from "react";
import ReactCrop, { type Crop } from "react-image-crop";
import { getCroppedImgAsFile } from "@/lib/canvas";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "../ui/form";
import { Thumbnail } from "../ui/tumbnail";

const formSchema = z.object({
  crop: z.custom<Crop>(),
});

export const ArtmatchCropImage = ({
  baseImage,
  onCancel,
  onNextStep,
}: {
  onCancel: () => void;
  onNextStep: (file: File) => void;
  baseImage: File;
}) => {
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
  });

  const baseImageUrl = useMemo(() => {
    return URL.createObjectURL(baseImage);
  }, [baseImage]);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [displayedSize, setDisplayedSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const target = e.currentTarget;
    setDisplayedSize({ width: target.width, height: target.height });
    imgRef.current = target;
  }

  function initCrop() {
    form.setValue("crop", {
      x: 0,
      y: 0,
      width: displayedSize?.width ?? 100,
      height: displayedSize?.height ?? 100,
      unit: "px",
    });
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!displayedSize) return;

    const crop =
      values.crop ??
      ({
        x: 0,
        y: 0,
        width: displayedSize.width,
        height: displayedSize.height,
        unit: "px",
      } as Crop);

    const croppedImage = await getCroppedImgAsFile(
      baseImageUrl,
      crop,
      displayedSize,
      0,
    );
    onNextStep(croppedImage);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader className="px-6 pt-6">
          <div className="flex gap-4 items-center">
            <Thumbnail
              onClick={initCrop}
              className="cursor-pointer hover:bg-gray-50"
            >
              <CropIcon className="text-gray-700 size-6" />
            </Thumbnail>
            <div className="flex flex-col w-full">
              <Title>{t("artmatch.cropImage.title")}</Title>
              <Subtitle>{t("artmatch.cropImage.subtitle")}</Subtitle>
            </div>
          </div>
        </DialogHeader>
        <div className="p-6">
          <Controller
            control={form.control}
            name="crop"
            render={({ field }) => (
              <ReactCrop
                onDragEnd={() => field.onBlur()}
                className="h-fit max-h-[512px]"
                crop={field.value}
                onChange={(c) => field.onChange(c)}
              >
                <img src={baseImageUrl} onLoad={handleImageLoad} />
              </ReactCrop>
            )}
          />
        </div>
        <CustomDialogFooter>
          <Button type="button" onClick={onCancel} variant="secondary">
            {t("common.cancel")}
          </Button>
          <Button type="submit">{t("artmatch.cropImage.submit")}</Button>
        </CustomDialogFooter>
      </form>
    </Form>
  );
};
